import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", payment_status: null, type: null });
  const attemptsRef = useRef(0);
  const { refresh } = useCart();
  const nav = useNavigate();

  useEffect(() => {
    if (!session_id) { setState({ phase: "error" }); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${session_id}`);
        if (data.payment_status === "paid") { setState({ phase: "paid", ...data }); refresh(); return; }
        if (data.status === "expired") { setState({ phase: "expired", ...data }); return; }
      } catch {}
      if (attemptsRef.current >= 8) { setState({ phase: "timeout" }); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [session_id, refresh]);

  return (
    <div data-testid="checkout-success-page" className="pt-32 pb-32 max-w-xl mx-auto px-4 text-center">
      {state.phase === "polling" && (<>
        <Loader2 className="w-10 h-10 text-[#05D9E8] mx-auto animate-spin" />
        <h1 className="font-display font-black text-3xl tracking-tight mt-6">Confirming your payment…</h1>
        <p className="text-zinc-500 text-sm mt-2">Hold tight, this only takes a few seconds.</p>
      </>)}
      {state.phase === "paid" && (<>
        <CheckCircle2 className="w-14 h-14 text-[#CCFF00] mx-auto" />
        <h1 className="font-display font-black text-4xl tracking-tight mt-6">Payment received.</h1>
        <p className="text-zinc-400 mt-2">Thanks for shopping the glow. Your order is confirmed.</p>
        <div className="flex justify-center gap-3 mt-8">
          <Link to="/browse" className="btn-secondary" data-testid="success-back-browse">Keep browsing</Link>
          <Link to="/" className="btn-primary" data-testid="success-back-home">Back home</Link>
        </div>
      </>)}
      {(state.phase === "expired" || state.phase === "error" || state.phase === "timeout") && (<>
        <XCircle className="w-12 h-12 text-red-400 mx-auto" />
        <h1 className="font-display font-black text-3xl tracking-tight mt-6">Payment not completed.</h1>
        <p className="text-zinc-500 mt-2">Please try again from your cart.</p>
        <button onClick={() => nav("/cart")} className="btn-primary mt-8" data-testid="success-retry-btn">Back to cart</button>
      </>)}
    </div>
  );
}
